import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

import { useAppStore } from "@/hooks/useAppStore";
import { useTranslation } from "@/lib/translate";

interface RoomHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RoomHistoryDialog: React.FC<RoomHistoryDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const redirectToRoom = useAppStore((state) => state.redirectToRoom);
  const user = useAppStore(state => state.user);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const roomId = useAppStore((state) => state.roomId);
  const clearRoomHistory = useAppStore((state) => state.clearRoomHistory);
  const roomHistory = useAppStore((state) => state.roomHistory);
  const isGuest = user && user.provider == "internal";
  const t = useTranslation(preferredLanguage);
  const onRoomClick = (roomId: string) => {
    onOpenChange(false);
    redirectToRoom(roomId);
  }

  const clearHistory = () => {
    if (confirm(t("Are you sure you want to clear room history?"))) {
      clearRoomHistory();
    }
  }
  const roomIds = Object.keys(roomHistory).sort((a, b) => { return roomHistory[b].updated - roomHistory[a].updated }).slice(0, 20);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            {t("Past Rooms")}
          </DialogTitle>
        </DialogHeader>

        <div className={`px-2 py-4 ${isGuest ? "opacity-5 pointer-events-none" : ""}`} style={{ "overflow": "scroll", maxHeight: "400px", display: "block", padding: "10p" }}>
          {roomIds.map((id, index) => {
            return id && <div key={id} className={"grid grid-cols-3 py-2 items-center" + (index < roomIds.length - 1 ? " border-b border-neutral-700" : "")}>
              <div className="col-span-2">
                <span><b>{id}</b></span>
                <div className="text-sm">{t("Joined:")} {new Date(roomHistory[id].updated).toDateString()}</div>
              </div>
              <Button
                className="hover:bg-neutral-400"
                type="button"
                variant="default"
                disabled={roomId == id}
                onClick={(e) => { onRoomClick(id); e.preventDefault(); }}>{roomId == id ? t("You're here") : t("Rejoin")}</Button>
            </div>
          })}
          {roomIds.length == 0 &&
            <div className="m-4 text-center">
              <div className="text-lg mb-4">No history found.</div>
              <div className="text-sm">You haven't joined any chat rooms.</div>
            </div>
          }
        </div>
        {isGuest && <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
          <a href="/login" className="text-blue-500 hover:text-blue-700">{t("Login")}</a> {t("to view room history.")}</div>}
        <DialogFooter className="sm:justify-right px-2">
          {roomIds.length > 0 && <Button className="bg-chotto-blue hover:bg-chotto-blue/90"
            type="button"
            variant="default"
            disabled={isGuest}
            onClick={(e) => { clearHistory(); e.preventDefault(); }}>
            {t("Clear History")}
          </Button>}
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}>
            {t("Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
